import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function RequestService() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    description: '',
    attachments: null as File[] | null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services = [
    'استشارات إدارية',
    'التخطيط الاستراتيجي',
    'تطوير الموارد البشرية',
    'الحلول التقنية',
    'التسويق الرقمي',
    'الاستشارات المالية'
  ];

  const budgetRanges = [
    'أقل من 10,000 ريال',
    '10,000 - 25,000 ريال',
    '25,000 - 50,000 ريال',
    '50,000 - 100,000 ريال',
    'أكثر من 100,000 ريال'
  ];

  const timelines = [
    'أقل من شهر',
    '1-3 أشهر',
    '3-6 أشهر',
    '6-12 شهر',
    'أكثر من سنة'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.description.length > 500) {
      alert('يجب أن لا يتجاوز الوصف 500 حرف');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('company', formData.company);
      submitData.append('service', formData.service);
      submitData.append('budget', formData.budget);
      submitData.append('timeline', formData.timeline);
      submitData.append('description', formData.description);
      submitData.append('attachments', 'غير قابل للتحصيل');

      const response = await fetch('https://readdy.ai/api/form/d4k61tt54aio6i8gekvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          budget: '',
          timeline: '',
          description: '',
          attachments: null
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">اطلب خدمة</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              املأ النموذج أدناه وسيتواصل معك فريقنا في أقرب وقت ممكن
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <form id="request-service-form" data-readdy-form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      الاسم الكامل <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm"
                      placeholder="أدخل اسمك الكامل"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      البريد الإلكتروني <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm"
                      placeholder="example@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      رقم الجوال <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm"
                      placeholder="05xxxxxxxx"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      اسم الشركة / المؤسسة
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm"
                      placeholder="اسم الشركة (اختياري)"
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      نوع الخدمة <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">اختر الخدمة</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      الميزانية المتوقعة
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">اختر الميزانية</option>
                      {budgetRanges.map((range, index) => (
                        <option key={index} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>

                  {/* Timeline */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-semibold mb-2">
                      الإطار الزمني المتوقع
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">اختر الإطار الزمني</option>
                      {timelines.map((timeline, index) => (
                        <option key={index} value={timeline}>{timeline}</option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-semibold mb-2">
                      وصف المشروع <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({...formData, description: e.target.value});
                        }
                      }}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-sm resize-none"
                      placeholder="اكتب وصفاً تفصيلياً عن المشروع أو الخدمة المطلوبة (الحد الأقصى 500 حرف)"
                    ></textarea>
                    <div className="text-left text-sm text-gray-500 mt-1">
                      {formData.description.length} / 500 حرف
                    </div>
                  </div>

                  {/* Attachments */}
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 font-semibold mb-2">
                      إرفاق ملفات
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                      <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-3"></i>
                      <p className="text-gray-600 mb-2">اسحب الملفات هنا أو انقر للتحميل</p>
                      <p className="text-sm text-gray-500">غير قابل للتحصيل</p>
                      <input
                        type="file"
                        name="attachments"
                        multiple
                        onChange={(e) => setFormData({...formData, attachments: e.target.files ? Array.from(e.target.files) : null})}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Status */}
                {submitStatus === 'success' && (
                  <div className="mb-6 bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-check-line text-2xl text-green-500"></i>
                    <p className="text-green-700 font-semibold">تم إرسال طلبك بنجاح! سنتواصل معك قريباً.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                    <p className="text-red-700 font-semibold">حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      <span>جاري الإرسال...</span>
                    </>
                  ) : (
                    <>
                      <span>إرسال الطلب</span>
                      <i className="ri-send-plane-fill"></i>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                  <i className="ri-phone-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">اتصل بنا</h3>
                <p className="text-gray-600" dir="ltr">+966 50 123 4567</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                  <i className="ri-mail-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">راسلنا</h3>
                <p className="text-gray-600">info@mustafa-mohamed.com</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                  <i className="ri-whatsapp-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">واتساب</h3>
                <p className="text-gray-600" dir="ltr">+966 50 123 4567</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}